// ============================================================================
// GERADOR DE "MEU PLANO" — duplicado em 16/09/2026 direto de
// fitclutch-mobile/src/config/planGenerator.js, sem alteração de lógica
// (só os imports apontam pros arquivos locais equivalentes). É o mesmo
// motor real usado no app pra montar o plano de vários dias — usado aqui
// só pro "Dia 1" de exemplo mostrado antes do paywall (PlanPreviewScreen).
// ============================================================================

import { EXERCISE_CATALOG, MUSCLE_GROUP_KEYS } from './exercisesConfig.js';
import { generateQuickWorkoutSlugs } from './quickWorkoutGenerator.js';

export { MUSCLE_GROUP_KEYS };

export const GOAL_KEYS = ['emagrecimento', 'hipertrofia', 'forca', 'condicionamento'];
export const SPLIT_KEYS = ['fullBody', 'upperLower', 'pushPullLegs', 'muscleGroup'];
export const DAYS_PER_WEEK_OPTIONS = [2, 3, 4, 5, 6];
export const INJURY_KEYS = ['shoulders', 'back', 'legs', 'forearms'];

export const GOAL_REST_SECONDS = {
  emagrecimento: 30,
  condicionamento: 30,
  hipertrofia: 75,
  forca: 120,
};

const UPPER_GROUPS = ['chest', 'back', 'shoulders', 'biceps', 'triceps'];
const LOWER_GROUPS = ['legs', 'glutes', 'abs'];
const PUSH_GROUPS = ['chest', 'shoulders', 'triceps'];
const PULL_GROUPS = ['back', 'biceps'];
const PPL_LEGS_GROUPS = ['legs', 'glutes', 'abs'];

const MUSCLE_GROUP_ROTATION = [
  ['chest'],
  ['back'],
  ['legs', 'glutes'],
  ['shoulders'],
  ['biceps', 'triceps', 'forearms'],
  ['abs'],
];

export function splitDayGroups(splitKey, daysPerWeek) {
  const n = Math.max(1, daysPerWeek);
  if (splitKey === 'upperLower') {
    return Array.from({ length: n }, (_, i) => (i % 2 === 0 ? UPPER_GROUPS : LOWER_GROUPS));
  }
  if (splitKey === 'pushPullLegs') {
    const rotation = [PUSH_GROUPS, PULL_GROUPS, PPL_LEGS_GROUPS];
    return Array.from({ length: n }, (_, i) => rotation[i % rotation.length]);
  }
  if (splitKey === 'muscleGroup') {
    return Array.from({ length: n }, (_, i) => MUSCLE_GROUP_ROTATION[i % MUSCLE_GROUP_ROTATION.length]);
  }
  return Array.from({ length: n }, () => []);
}

export function prioritizeMuscleFocus(splitKey, daysPerWeek, muscleFocus) {
  if (splitKey !== 'muscleGroup' || !muscleFocus?.length) {
    return splitDayGroups(splitKey, daysPerWeek);
  }
  const priority = MUSCLE_GROUP_ROTATION.filter((combo) => combo.some((g) => muscleFocus.includes(g)));
  const rest = MUSCLE_GROUP_ROTATION.filter((combo) => !priority.includes(combo));
  const rotation = [...priority, ...rest];
  const n = Math.max(1, daysPerWeek);
  return Array.from({ length: n }, (_, i) => rotation[i % rotation.length]);
}

function primaryGroup(slug) {
  return EXERCISE_CATALOG[slug]?.muscleGroup ?? null;
}
function allGroups(slug) {
  const ex = EXERCISE_CATALOG[slug];
  if (!ex) return [];
  return [ex.muscleGroup, ...(ex.secondaryMuscleGroups ?? [])].filter(Boolean);
}

function filterByInjuries(slugs, injuries) {
  if (!injuries?.length) return slugs;
  const injurySet = new Set(injuries);
  return slugs.filter((slug) => !allGroups(slug).some((g) => injurySet.has(g)));
}

function applyBoosts(slugs, dayGroups, boostedSlugs, fullBody) {
  if (!boostedSlugs?.length) return slugs;
  const groupsInDay = fullBody ? null : new Set(dayGroups);
  const result = [...slugs];
  const seen = new Set(result);
  let extrasAdded = 0;
  for (const boosted of boostedSlugs) {
    if (seen.has(boosted)) continue;
    const group = primaryGroup(boosted);
    if (!group || (groupsInDay && !groupsInDay.has(group))) continue;
    const swapIdx = result.findIndex((s) => primaryGroup(s) === group && !boostedSlugs.includes(s));
    if (swapIdx !== -1) {
      seen.delete(result[swapIdx]);
      result[swapIdx] = boosted;
      seen.add(boosted);
    } else if (extrasAdded < 2) {
      result.push(boosted);
      seen.add(boosted);
      extrasAdded++;
    }
  }
  return result;
}

export function generateWorkoutPlan({
  daysPerWeek = 3,
  splitKey = 'fullBody',
  goalKey = 'hipertrofia',
  durationMinutes = 40,
  levelKey = 'intermediate',
  placeKey = 'large',
  customEquipment = [],
  muscleFocus = [],
  injuries = [],
  boostedSlugs = [],
}) {
  const dayGroupsList = prioritizeMuscleFocus(splitKey, daysPerWeek, muscleFocus);
  const fullBody = splitKey === 'fullBody';

  const days = dayGroupsList.map((groups) => {
    const generated = generateQuickWorkoutSlugs({
      durationMinutes,
      levelKey,
      placeKey,
      customEquipment,
      muscleGroups: groups,
      fullBody,
    });
    const afterInjuries = filterByInjuries(generated.slugs, injuries);
    const finalSlugs = applyBoosts(afterInjuries, groups, boostedSlugs, fullBody);
    return {
      muscleGroups: groups,
      slugs: finalSlugs,
      estimatedMinutes: finalSlugs.length * 7,
      supersets: {},
      exerciseRestSeconds: {},
    };
  });

  return {
    days: days.map((d) => ({ ...d, regenCount: 0 })),
    restSecondsDefault: GOAL_REST_SECONDS[goalKey] ?? 60,
  };
}

export const VARIABILITY_KEYS = ['baixa', 'media', 'alta'];
const VARIABILITY_STEP = { baixa: 1, media: 2, alta: 4 };

export function regeneratePlanDay({
  dayIndex,
  daysPerWeek,
  splitKey,
  durationMinutes,
  levelKey,
  placeKey,
  customEquipment,
  muscleFocus,
  injuries,
  boostedSlugs,
  variability = 'media',
  regenCount = 0,
}) {
  const dayGroupsList = prioritizeMuscleFocus(splitKey, daysPerWeek, muscleFocus);
  const groups = dayGroupsList[dayIndex] ?? [];
  const fullBody = splitKey === 'fullBody';
  const poolOffset = regenCount * (VARIABILITY_STEP[variability] ?? VARIABILITY_STEP.media);
  const generated = generateQuickWorkoutSlugs({
    durationMinutes,
    levelKey,
    placeKey,
    customEquipment,
    muscleGroups: groups,
    fullBody,
    poolOffset,
  });
  const afterInjuries = filterByInjuries(generated.slugs, injuries);
  const finalSlugs = applyBoosts(afterInjuries, groups, boostedSlugs, fullBody);
  return {
    muscleGroups: groups,
    slugs: finalSlugs,
    estimatedMinutes: finalSlugs.length * 7,
    supersets: {},
    exerciseRestSeconds: {},
    regenCount: regenCount + 1,
  };
}
